import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import {
  toLocalDate,
  toLocalTime,
  combineDateAndTime,
  dateTimeFieldsFromIso,
} from 'dashboard/components-next/planning/Scheduler/allocationDateTimeFields';
import {
  resolveParentAllocation,
  updateParentAllocationWithChildSync,
  hasSubAllocations,
} from 'dashboard/components-next/planning/Scheduler/allocationParentChildSync';
import { buildAllocationApiPayload } from 'dashboard/components-next/planning/Scheduler/allocationPayload';
import { ALLOCATION_TIME_SNAP_MINUTES } from 'dashboard/components-next/planning/Scheduler/allocationTimeSnap';
import { getEventDateRange } from './getEventDateRange';

const SNAP_MS = ALLOCATION_TIME_SNAP_MINUTES * 60 * 1000;

function roundUpToMinutes(date) {
  return new Date(Math.ceil(date.getTime() / SNAP_MS) * SNAP_MS);
}

function defaultDateTimeFields() {
  const start = roundUpToMinutes(new Date(Date.now() + 86_400_000));
  const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
  return dateTimeFieldsFromIso(start.toISOString(), end.toISOString());
}

function resolvePrimaryAllocation(event) {
  const allocations = event?.allocations || [];
  const parent = resolveParentAllocation(allocations);
  if (parent) return parent;

  const roots = allocations.filter(a => !a.parent_allocation_id);
  if (roots.length === 1) return roots[0];

  const firstAllocated = event?.allocated_resources?.[0];
  if (firstAllocated?.id) {
    return allocations.find(a => a.id === firstAllocated.id) || null;
  }

  return null;
}

function dateTimeFieldsFromEvent(event, primaryAllocation) {
  if (primaryAllocation?.start_at && primaryAllocation?.end_at) {
    return dateTimeFieldsFromIso(
      primaryAllocation.start_at,
      primaryAllocation.end_at
    );
  }

  const range = getEventDateRange(event);
  if (range?.start && range?.end) {
    return dateTimeFieldsFromIso(
      range.start.toISOString(),
      range.end.toISOString()
    );
  }

  return defaultDateTimeFields();
}

function defaultResourceId(event, accountSpaces) {
  const primaryAllocation = resolvePrimaryAllocation(event);
  if (primaryAllocation?.resource_id) return primaryAllocation.resource_id;

  const firstAllocated = event?.allocated_resources?.[0];
  if (firstAllocated?.resource_id) return firstAllocated.resource_id;

  return accountSpaces?.find(space => space.resource_id)?.resource_id || null;
}

export function useEventKeyInfoEditing({
  eventId,
  event,
  canEditEvent,
  accountSpaces,
  onEventUpdated,
}) {
  const store = useStore();

  const editingDate = ref(false);
  const editingTime = ref(false);
  const editingLocation = ref(false);
  const tempStartDate = ref('');
  const tempStartTime = ref('');
  const tempEndDate = ref('');
  const tempEndTime = ref('');
  const tempResourceId = ref(null);
  const savingKeyInfo = ref(false);

  const planningResources = computed(
    () => store.getters['planningResources/getAllocatableResources']
  );

  const primaryAllocation = computed(() =>
    resolvePrimaryAllocation(event.value)
  );

  const canEditKeyInfo = computed(() => canEditEvent.value);

  const loadTempDateTime = () => {
    const fields = dateTimeFieldsFromEvent(
      event.value,
      primaryAllocation.value
    );
    tempStartDate.value = fields.startDate;
    tempStartTime.value = fields.startTime;
    tempEndDate.value = fields.endDate;
    tempEndTime.value = fields.endTime;
  };

  const loadTempResourceId = () => {
    tempResourceId.value = defaultResourceId(event.value, accountSpaces.value);
  };

  const startEditingDate = () => {
    if (!canEditKeyInfo.value) return;
    loadTempDateTime();
    editingDate.value = true;
    editingTime.value = false;
    editingLocation.value = false;
  };

  const startEditingTime = () => {
    if (!canEditKeyInfo.value) return;
    loadTempDateTime();
    editingTime.value = true;
    editingDate.value = false;
    editingLocation.value = false;
  };

  const startEditingLocation = () => {
    if (!canEditKeyInfo.value) return;
    loadTempResourceId();
    editingLocation.value = true;
    editingDate.value = false;
    editingTime.value = false;
  };

  const cancelEditingDate = () => {
    editingDate.value = false;
  };

  const cancelEditingTime = () => {
    editingTime.value = false;
  };

  const cancelEditingLocation = () => {
    editingLocation.value = false;
  };

  const validateDateTime = () => {
    try {
      const startAt = combineDateAndTime(
        tempStartDate.value,
        tempStartTime.value
      );
      const endAt = combineDateAndTime(tempEndDate.value, tempEndTime.value);
      if (new Date(startAt) >= new Date(endAt)) return null;
      return { startAt, endAt };
    } catch {
      return null;
    }
  };

  const refreshEvent = async () => {
    const updated = await store.dispatch('planningEvents/show', eventId.value);
    onEventUpdated?.(updated);
    return updated;
  };

  const createAllocationWithKeyInfo = async ({
    startAt,
    endAt,
    resourceId,
  }) => {
    if (!resourceId) return null;

    await store.dispatch(
      'planningAllocations/create',
      buildAllocationApiPayload(
        {
          resource_id: resourceId,
          start_at: startAt,
          end_at: endAt,
          buffer_before_min: 0,
          buffer_after_min: 0,
        },
        {
          title: event.value?.name,
          event_id: eventId.value,
        }
      )
    );

    return refreshEvent();
  };

  const saveDateTime = async () => {
    const validated = validateDateTime();
    if (!validated) return;

    const { startAt, endAt } = validated;
    savingKeyInfo.value = true;

    try {
      const allocation = primaryAllocation.value;

      if (allocation) {
        if (hasSubAllocations(allocation)) {
          await updateParentAllocationWithChildSync({
            store,
            parentAllocation: allocation,
            newStartAt: startAt,
            newEndAt: endAt,
          });
        } else {
          await store.dispatch('planningAllocations/update', {
            id: allocation.id,
            start_at: startAt,
            end_at: endAt,
          });
        }
        await refreshEvent();
      } else {
        loadTempResourceId();
        if (!tempResourceId.value) {
          startEditingLocation();
          return;
        }
        await createAllocationWithKeyInfo({
          startAt,
          endAt,
          resourceId: tempResourceId.value,
        });
      }

      editingDate.value = false;
      editingTime.value = false;
    } finally {
      savingKeyInfo.value = false;
    }
  };

  const saveLocation = async () => {
    if (!tempResourceId.value) return;

    savingKeyInfo.value = true;

    try {
      const allocation = primaryAllocation.value;

      if (allocation) {
        await store.dispatch('planningAllocations/update', {
          id: allocation.id,
          resource_id: tempResourceId.value,
        });
        await refreshEvent();
      } else {
        const fields = dateTimeFieldsFromEvent(
          event.value,
          primaryAllocation.value
        );
        const startAt = combineDateAndTime(fields.startDate, fields.startTime);
        const endAt = combineDateAndTime(fields.endDate, fields.endTime);
        await createAllocationWithKeyInfo({
          startAt,
          endAt,
          resourceId: tempResourceId.value,
        });
      }

      editingLocation.value = false;
    } finally {
      savingKeyInfo.value = false;
    }
  };

  return {
    editingDate,
    editingTime,
    editingLocation,
    tempStartDate,
    tempStartTime,
    tempEndDate,
    tempEndTime,
    tempResourceId,
    canEditKeyInfo,
    planningResources,
    savingKeyInfo,
    startEditingDate,
    startEditingTime,
    startEditingLocation,
    cancelEditingDate,
    cancelEditingTime,
    cancelEditingLocation,
    saveDateTime,
    saveLocation,
  };
}
